import React from 'react';
import { defaultUpdateLocation } from '../update_params';

// The slice of a location updateUrl needs; satisfied by react-router's
// useLocation() and by useURL() from url_context.
export interface UpdateUrlLocation {
  pathname: string;
  search: string;
}

export interface UpdateUrlArgs {
  location: UpdateUrlLocation,
  newParams: PlainObject,
  anchor?: string,
  autodirects?: boolean,
  correctedTo?: string,
  corrected?: boolean;
}

// A target react-router v7's navigate()/<Link to> understands directly.
export interface UpdateUrlResult {
  pathname: string;
  search: string;
  hash?: string;
}

export type UpdateUrlType = (args: UpdateUrlArgs) => UpdateUrlResult;

export interface UpdateUrlContextType {
  updateUrl: UpdateUrlType;
}

export const UpdateUrlContext = React.createContext<UpdateUrlContextType>({
  updateUrl: defaultUpdateLocation,
});

interface IUpdateURLProviderProps {
  updateUrl?: UpdateUrlType;
  children: React.ReactNode;
}

export function UpdateURLProvider({ updateUrl = defaultUpdateLocation, children }: IUpdateURLProviderProps) {
  return (
    <UpdateUrlContext.Provider value={{ updateUrl }}>
      {children}
    </UpdateUrlContext.Provider>
  );
}

export function useUpdateUrl(): UpdateUrlContextType {
  return React.useContext(UpdateUrlContext);
}

export interface IUpdateURLProps {
  updateUrl: UpdateUrlType;
}

export function withUpdateURL<TProps extends {}>(
  Component: React.ComponentType<TProps & IUpdateURLProps>,
): React.ComponentType<TProps> {

  const result = class Connected extends React.Component<TProps, null> {
    static displayName = `withUpdateURL(${Component.displayName || Component.name})`;

    static WrappedComponent = Component;

    static contextType = UpdateUrlContext;

    declare context: UpdateUrlContextType;

    render() {
      const updateUrl = this.context.updateUrl || defaultUpdateLocation;

      return (<Component updateUrl={updateUrl} {...this.props} />);
    }
  };

  return result;
}
